// Colombian business-day calculator for cloud functions (CommonJS twin of
// src/utils/businessDays.js). Keep the two files in sync.
//
// Holidays are computed from Ley Emiliani rules + Easter (Meeus/Jones/Butcher).

const BOGOTA_TZ = "America/Bogota";

const DATE_KEY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: BOGOTA_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: BOGOTA_TZ,
  weekday: "short",
});

const toBogotaDateKey = (date) => DATE_KEY_FORMATTER.format(date);
const bogotaWeekday = (date) => WEEKDAY_FORMATTER.format(date);

const ymdKey = (y, m, d) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const addDays = (ymd, days) => {
  const base = Date.UTC(ymd.year, ymd.month - 1, ymd.day);
  const next = new Date(base + days * 86400000);
  return {
    year: next.getUTCFullYear(),
    month: next.getUTCMonth() + 1,
    day: next.getUTCDate(),
  };
};

const mondayOnOrAfter = (ymd) => {
  const base = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day));
  const dow = base.getUTCDay();
  const shift = dow === 1 ? 0 : (8 - dow) % 7;
  const shifted = addDays(ymd, shift);
  return ymdKey(shifted.year, shifted.month, shifted.day);
};

const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { year, month, day };
};

const HOLIDAYS_CACHE = new Map();
const HOLIDAY_LIST_CACHE = new Map();

// The 18 statutory holidays, named. Twelve of them are "traslativos": the Ley
// Emiliani moves them to the following Monday, which is why a date that looks
// wrong on the calendar (Reyes on the 12th, not the 6th) is usually right.
const colombianHolidayList = (year) => {
  if (HOLIDAY_LIST_CACHE.has(year)) return HOLIDAY_LIST_CACHE.get(year);
  const easter = easterSunday(year);
  const fromYmd = (ymd) => ymdKey(ymd.year, ymd.month, ymd.day);

  const list = [
    { date: ymdKey(year, 1, 1), name: "Año Nuevo", moved: false },
    { date: mondayOnOrAfter({ year, month: 1, day: 6 }), name: "Día de los Reyes Magos", moved: true },
    { date: mondayOnOrAfter({ year, month: 3, day: 19 }), name: "Día de San José", moved: true },
    { date: fromYmd(addDays(easter, -3)), name: "Jueves Santo", moved: false },
    { date: fromYmd(addDays(easter, -2)), name: "Viernes Santo", moved: false },
    { date: ymdKey(year, 5, 1), name: "Día del Trabajo", moved: false },
    { date: mondayOnOrAfter(addDays(easter, 39)), name: "Ascensión del Señor", moved: true },
    { date: mondayOnOrAfter(addDays(easter, 60)), name: "Corpus Christi", moved: true },
    { date: mondayOnOrAfter(addDays(easter, 68)), name: "Sagrado Corazón de Jesús", moved: true },
    { date: mondayOnOrAfter({ year, month: 6, day: 29 }), name: "San Pedro y San Pablo", moved: true },
    { date: ymdKey(year, 7, 20), name: "Día de la Independencia", moved: false },
    { date: ymdKey(year, 8, 7), name: "Batalla de Boyacá", moved: false },
    { date: mondayOnOrAfter({ year, month: 8, day: 15 }), name: "La Asunción de la Virgen", moved: true },
    { date: mondayOnOrAfter({ year, month: 10, day: 12 }), name: "Día de la Raza", moved: true },
    { date: mondayOnOrAfter({ year, month: 11, day: 1 }), name: "Todos los Santos", moved: true },
    { date: mondayOnOrAfter({ year, month: 11, day: 11 }), name: "Independencia de Cartagena", moved: true },
    { date: ymdKey(year, 12, 8), name: "Inmaculada Concepción", moved: false },
    { date: ymdKey(year, 12, 25), name: "Navidad", moved: false },
  ].sort((a, b) => (a.date < b.date ? -1 : 1));

  HOLIDAY_LIST_CACHE.set(year, list);
  return list;
};

const colombianHolidays = (year) => {
  if (HOLIDAYS_CACHE.has(year)) return HOLIDAYS_CACHE.get(year);
  const keys = new Set(colombianHolidayList(year).map((h) => h.date));
  HOLIDAYS_CACHE.set(year, keys);
  return keys;
};

// --- institutional calendar --------------------------------------------------
//
// The statutory table is only half the story. An institution closes on days the
// law knows nothing about — Semana Santa in full, the week between Christmas
// and New Year, a local fair, a strike — and occasionally works a day the law
// calls a holiday. Both are set in the general configuration (config/global
// .calendar) and applied here, so every term computed anywhere in the app
// agrees with the door being locked.
//
// Deliberately a module-level override rather than a parameter: the calculator
// is called from deep inside pure logic, and threading a calendar through every
// caller would be worse than loading it once per instance.
let CLOSED_DAYS = new Set();
let OPEN_DAYS = new Set();

// A shape check is not enough: "2026-13-45" matches the pattern and would sit
// in the calendar forever without ever being a day, so the date has to survive
// a round trip through the calendar it claims to be from.
const isRealDayKey = (key) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return false;
  const [y, m, d] = key.split("-").map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y
    && probe.getUTCMonth() === m - 1
    && probe.getUTCDate() === d;
};

// A sane ceiling on how many days one institution can declare. Anything past
// this is a paste accident, not a calendar.
const MAX_CALENDAR_DAYS = 2000;

const normalizeDayKeys = (days) => {
  const out = new Set();
  (Array.isArray(days) ? days : []).slice(0, MAX_CALENDAR_DAYS).forEach((d) => {
    const key = typeof d === "string" ? d.trim().slice(0, 10) : "";
    if (isRealDayKey(key)) out.add(key);
  });
  return out;
};

/**
 * Applies the institution's own calendar.
 * @param {object} calendar
 * @param {string[]} calendar.closedDays - extra non-working days (YYYY-MM-DD).
 * @param {string[]} calendar.openDays - days worked despite being a holiday or
 *   a weekend. Takes precedence, so a mistake is always correctable.
 */
const setCustomCalendar = ({ closedDays = [], openDays = [] } = {}) => {
  CLOSED_DAYS = normalizeDayKeys(closedDays);
  OPEN_DAYS = normalizeDayKeys(openDays);
};

const getCustomCalendar = () => ({
  closedDays: [...CLOSED_DAYS].sort(),
  openDays: [...OPEN_DAYS].sort(),
});

const isBusinessDay = (date) => {
  const key = toBogotaDateKey(date);
  // An explicit working day wins over everything, including a Sunday: that is
  // the only way to undo a mistake in the two lists below without a deploy.
  if (OPEN_DAYS.has(key)) return true;
  if (CLOSED_DAYS.has(key)) return false;
  const wd = bogotaWeekday(date);
  if (wd === "Sat" || wd === "Sun") return false;
  const year = parseInt(key.slice(0, 4), 10);
  return !colombianHolidays(year).has(key);
};

// Ten years of calendar days. Before the institutional calendar these walks
// could never run long — weekends and holidays cluster — but the exit condition
// is now admin-supplied data, and a pasted decade of closures would turn every
// SLA computation into a long loop (the sweep runs this over every open PQRSD).
// A bounded wrong answer with a loud log beats a function that never returns.
const MAX_WALK_DAYS = 3660;

const addBusinessDays = (date, n) => {
  if (!Number.isFinite(n) || n === 0) return new Date(date.getTime());
  const step = n > 0 ? 1 : -1;
  let remaining = Math.abs(n);
  let cursor = new Date(date.getTime());
  let walked = 0;
  while (remaining > 0) {
    if (walked++ >= MAX_WALK_DAYS) {
      console.warn(
        "[businessDays] gave up after", MAX_WALK_DAYS,
        "days; the institutional calendar leaves almost no working days"
      );
      break;
    }
    cursor = new Date(cursor.getTime() + step * 86400000);
    if (isBusinessDay(cursor)) remaining -= 1;
  }
  return cursor;
};

const diffBusinessDays = (a, b) => {
  if (a.getTime() === b.getTime()) return 0;
  const forward = a < b;
  const start = forward ? a : b;
  const end = forward ? b : a;
  let count = 0;
  let cursor = new Date(start.getTime());
  while (cursor < end) {
    cursor = new Date(cursor.getTime() + 86400000);
    if (isBusinessDay(cursor)) count += 1;
  }
  return forward ? count : -count;
};

// Reads the institution's calendar from config/global and applies it. Cached
// per warm instance with a short TTL, so a day added in the settings screen
// takes effect within minutes without a redeploy. A read failure leaves the
// previously loaded calendar in place rather than silently reopening the doors.
// Ten minutes by default: long enough that the read is free in practice, short
// enough that a day added in the settings screen takes effect without a
// redeploy. BUSINESS_CALENDAR_TTL_MS overrides it (0 = always re-read, which is
// what the tests use).
const CALENDAR_TTL_MS = Number.isFinite(Number(process.env.BUSINESS_CALENDAR_TTL_MS))
  ? Number(process.env.BUSINESS_CALENDAR_TTL_MS)
  : 10 * 60 * 1000;
let calendarPromise = null;
let calendarLoadedAt = 0;

async function loadCustomCalendar(firestore, { ttlMs = CALENDAR_TTL_MS } = {}) {
  if (calendarPromise && Date.now() - calendarLoadedAt < ttlMs) return calendarPromise;
  calendarLoadedAt = Date.now();
  calendarPromise = (async () => {
    try {
      const snap = await firestore.collection("config").doc("global").get();
      const calendar = (snap.exists ? snap.data().calendar : null) || {};
      setCustomCalendar(calendar);
    } catch (e) {
      console.warn("[businessDays] institutional calendar unavailable:", e && e.message);
    }
    return getCustomCalendar();
  })();
  return calendarPromise;
}

module.exports = {
  colombianHolidays,
  loadCustomCalendar,
  colombianHolidayList,
  setCustomCalendar,
  getCustomCalendar,
  isBusinessDay,
  addBusinessDays,
  diffBusinessDays,
  toBogotaDateKey,
};
